import { PayloadAction, Slice, createSlice } from '@reduxjs/toolkit';
import { Reseller, ResellerInitialState } from '@/types/TReseller';
import posthog from 'posthog-js';
import { cloneDeep } from 'lodash';

const initialState: ResellerInitialState = {
	data: {
		id: '',
		username: '',
		inboundDemo: false,
		outboundDemo: false,
		userType: 'Reseller',
		totalInboundAgents: 0,
		totalOutboundAgents: 0,
		voiceSubscriptionStatus: '',
		inboundSubscribedProductId: '',
		outboundSubscribedProductId: '',
		inboundSubscriptionId: '',
		outboundSubscriptionId: '',
		auto_scale_receptionists: false,
		unConfiguredInboundAgents: 0,
		unConfiguredOutboundAgents: 0,
		customDomain: '',
		timestamp: '',
		reseller_type: '',
		theme_color: '',
		emailAddress: '',
		termsOfUseURL: '',
		inboundScheduleSubCancelOn: '',
		outboundScheduleSubCancelOn: '',
		privacyPolicyURL: '',
		voiceSubscriptionId: '',
		logo: '',
		businessName: '',
		configuredInboundAgents: 0,
		configuredOutboundAgents: 0,
		inboundSubscriptionStatus: '',
		outboundSubscriptionStatus: '',
		customerId: '',
		branded: false,
		resellerView: true,
	},
	clients: [],
	isAuthenticated: false,
	authToken: undefined,
	isTrialReseller: false,
};

export const slice: Slice<
	ResellerInitialState,
	{
		setResellerAccountInfo: (
			state: ResellerInitialState,
			action: PayloadAction<ResellerInitialState>,
		) => void;
		setReseller: (state: ResellerInitialState, action: PayloadAction<Reseller>) => void;
		updateReseller: (state: ResellerInitialState, action: PayloadAction<Partial<Reseller>>) => void;
		updateResellerClients: (
			state: ResellerInitialState,
			action: PayloadAction<Partial<any[]>>,
		) => void;
		setResellerView: (state: ResellerInitialState, action: PayloadAction<boolean>) => void;
		setTrialStatus: (state: ResellerInitialState, action: PayloadAction<boolean>) => void;
	},
	'reseller'
> = createSlice({
	name: 'reseller',
	initialState,
	reducers: {
		setResellerAccountInfo: (
			state: ResellerInitialState,
			action: PayloadAction<ResellerInitialState>,
		) => {
			state.data = action.payload.data;
			state.authToken = action.payload.authToken;
			state.isAuthenticated = action.payload.isAuthenticated;

			// Identify user with PostHog if authenticated
			if (state.isAuthenticated && state.data.username) {
				posthog.identify(state.data.username, {
					...state.data,
					createdAt: new Date(state.data?.createdAt || '2025-01-01T20:11:42.779Z').getTime(),
				});

				const username = state.data.username;
				const deepCopy = cloneDeep(state.data);

				window.clarity('identify', username, null, null, username);
				Object.entries(deepCopy).forEach(([key, value]) => {
					if (value !== undefined && value !== null) {
						window.clarity('set', key, value);
					}
				});
			}
		},
		setReseller: (state: ResellerInitialState, action: PayloadAction<Reseller>) => {
			state.data = action.payload;
			state.authToken = action.payload.token;
			state.isAuthenticated = !!action.payload.token;
		},
		updateReseller(state: ResellerInitialState, action: PayloadAction<Partial<Reseller>>) {
			state.data = { ...state.data, ...action.payload };
		},
		updateResellerClients(state: ResellerInitialState, action: PayloadAction<Partial<any[]>>) {
			state.clients = action.payload;
		},
		setResellerView(state: ResellerInitialState, action: PayloadAction<boolean>) {
			state.data.resellerView = action.payload;
		},
		setTrialStatus(state: ResellerInitialState, action: PayloadAction<boolean>) {
			state.isTrialReseller = action.payload;
		},
	},
});

export const {
	setResellerAccountInfo,
	setReseller,
	updateReseller,
	updateResellerClients,
	setResellerView,
	setTrialStatus,
} = slice.actions;

export default slice.reducer;
